import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_COMMITTEEMEETINGATTENDANCE_LOAD_BY_ID,
    ACTION_COMMITTEEMEETINGATTENDANCE_LOAD_BY_COMMITTEEMEETING_ID,
    ACTION_COMMITTEEMEETINGATTENDANCE_LOAD_FAILURE,
    ACTION_COMMITTEEMEETINGATTENDANCE_LOAD_SUCCESS,

    ACTION_COMMITTEEMEETINGATTENDANCE_SAVE,
    ACTION_COMMITTEEMEETINGATTENDANCE_SAVE_SUCCESS,
    ACTION_COMMITTEEMEETINGATTENDANCE_SAVE_FAILURE,

    ACTION_COMMITTEEMEETINGATTENDANCE_CREATE,
    ACTION_COMMITTEEMEETINGATTENDANCE_CREATE_SUCCESS,
    ACTION_COMMITTEEMEETINGATTENDANCE_CREATE_FAILURE,

    ACTION_COMMITTEEMEETINGATTENDANCE_VALIDATE,
    ACTION_COMMITTEEMEETINGATTENDANCE_VALIDATE_SUCCESS,
    ACTION_COMMITTEEMEETINGATTENDANCE_VALIDATE_FAILURE,

    ACTION_COMMITTEEMEETINGATTENDANCE_CLEAR,
    ACTION_COMMITTEEMEETINGATTENDANCE_FILTER,

    CommitteeMeetingAttendanceAction
} from '../actions/committeeMeetingAttendance';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ICommitteeMeetingAttendanceState {
    formData: Dtos.CommitteeMeetingAttendance | Dtos.CommitteeMeetingAttendance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.CommitteeMeetingAttendance[];
    formLength: number;
}

const initialState: ICommitteeMeetingAttendanceState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ICommitteeMeetingAttendanceState, _isLoading: boolean): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ICommitteeMeetingAttendanceState,
    formData: Dtos.CommitteeMeetingAttendance | Dtos.CommitteeMeetingAttendance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ICommitteeMeetingAttendanceState, responseStatus: Dtos.ResponseStatus): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ICommitteeMeetingAttendanceState, formFilter: IFormFilter): ICommitteeMeetingAttendanceState {

    const items: Dtos.CommitteeMeetingAttendance[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            (p.comment && p.comment.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
        )) : undefined;

    const newState: ICommitteeMeetingAttendanceState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ICommitteeMeetingAttendanceState, propertiesToClear?: string[]): ICommitteeMeetingAttendanceState {

    if (!propertiesToClear) {
        const newState: ICommitteeMeetingAttendanceState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ICommitteeMeetingAttendanceState, _isSaving: boolean): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ICommitteeMeetingAttendanceState,
    formData: Dtos.CommitteeMeetingAttendance | Dtos.CommitteeMeetingAttendance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ICommitteeMeetingAttendanceState, responseStatus: Dtos.ResponseStatus): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ICommitteeMeetingAttendanceState, _isCreating: boolean): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ICommitteeMeetingAttendanceState,
    formData: Dtos.CommitteeMeetingAttendance,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ICommitteeMeetingAttendanceState, responseStatus: Dtos.ResponseStatus): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ICommitteeMeetingAttendanceState, _isValidating: boolean): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ICommitteeMeetingAttendanceState,
    formData: Dtos.CommitteeMeetingAttendance,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ICommitteeMeetingAttendanceState, responseStatus: Dtos.ResponseStatus): ICommitteeMeetingAttendanceState {
    const newState: ICommitteeMeetingAttendanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const CommitteeMeetingAttendanceReducer: Reducer<ICommitteeMeetingAttendanceState> = (state: ICommitteeMeetingAttendanceState = initialState, action: CommitteeMeetingAttendanceAction) => {
    switch (action.type) {
        case ACTION_COMMITTEEMEETINGATTENDANCE_LOAD_BY_ID:
        case ACTION_COMMITTEEMEETINGATTENDANCE_LOAD_BY_COMMITTEEMEETING_ID:
            return loading(state, true);
        case ACTION_COMMITTEEMEETINGATTENDANCE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_COMMITTEEMEETINGATTENDANCE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEETINGATTENDANCE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_COMMITTEEMEETINGATTENDANCE_SAVE:
            return saving(state, true);
        case ACTION_COMMITTEEMEETINGATTENDANCE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEMEETINGATTENDANCE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEETINGATTENDANCE_CREATE:
            return creating(state, true);
        case ACTION_COMMITTEEMEETINGATTENDANCE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_COMMITTEEMEETINGATTENDANCE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEETINGATTENDANCE_VALIDATE:
            return validating(state, true);
        case ACTION_COMMITTEEMEETINGATTENDANCE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_COMMITTEEMEETINGATTENDANCE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_COMMITTEEMEETINGATTENDANCE_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default CommitteeMeetingAttendanceReducer;